import { Button, mergeClasses } from '@fluentui/react-components';
import {
  bundleIcon,
  ArrowDownload24Filled,
  ArrowDownload24Regular,
  ArrowMaximize24Filled,
  ArrowMaximize24Regular,
  Checkmark16Filled,
  Checkmark16Regular,
  ChevronDown12Regular,
  ChevronRight12Regular,
  Copy16Filled,
  Copy16Regular,
  Copy24Filled,
  Copy24Regular,
} from '@fluentui/react-icons';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';

import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard/useCopyToClipboard';
import { repairTruncatedJson } from '../../../utils/sigsParser.js';
import { MaximizeDialog } from '../../MaximizeDialog/MaximizeDialog.js';

import type { RawJsonViewerProps } from './RawJsonViewer.interface';
import { useStyles } from './RawJsonViewer.style';

const DownloadIcon = bundleIcon(
  ArrowDownload24Filled,
  ArrowDownload24Regular,
) as React.FC;
const CopyIcon = bundleIcon(Copy24Filled, Copy24Regular) as React.FC;
const CopySmallIcon = bundleIcon(Copy16Filled, Copy16Regular) as React.FC;
const CheckSmallIcon = bundleIcon(
  Checkmark16Filled,
  Checkmark16Regular,
) as React.FC;
const MaximizeIcon = bundleIcon(
  ArrowMaximize24Filled,
  ArrowMaximize24Regular,
) as React.FC;

const INDENT_PX = 16;

function parseReferencedDocuments(value: string): unknown {
  let candidate = value.replace(/<truncated>$/, '');

  for (let depth = 0; depth < 2; depth += 1) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(candidate) as unknown;
    } catch {
      try {
        parsed = JSON.parse(repairTruncatedJson(candidate)) as unknown;
      } catch {
        return value;
      }
    }

    if (parsed !== null && typeof parsed === 'object') {
      return parsed;
    }
    if (typeof parsed !== 'string') {
      return value;
    }
    candidate = parsed;
  }

  return value;
}

// ---------------------------------------------------------------------------
// Row wrapper with hover highlight and copy button
// ---------------------------------------------------------------------------

interface RowLineProps {
  children: React.ReactNode;
  depth: number;
  copyValue: string;
  collapsible?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
}

function RowLine({
  children,
  depth,
  copyValue,
  collapsible,
  collapsed,
  onToggle,
}: RowLineProps) {
  const styles = useStyles();
  const [hovered, setHovered] = useState(false);
  const { copied, copy } = useCopyToClipboard();

  return (
    <div
      className={mergeClasses(styles.row, hovered && styles.rowHover)}
      style={{ paddingLeft: depth * INDENT_PX }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {collapsible ? (
        <button
          className={styles.toggle}
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronRight12Regular /> : <ChevronDown12Regular />}
        </button>
      ) : (
        <span className={styles.spacer} />
      )}
      {children}
      {hovered && (
        <button
          type="button"
          className={styles.copyBtn}
          onClick={() => copy(copyValue)}
          title="Copy value"
        >
          {copied ? <CheckSmallIcon /> : <CopySmallIcon />}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Collapsible JSON Tree Node
// ---------------------------------------------------------------------------

interface JsonNodeProps {
  keyName?: string;
  value: unknown;
  depth: number;
  defaultExpanded: boolean;
  isLast: boolean;
}

function JsonNode({
  keyName,
  value,
  depth,
  defaultExpanded,
  isLast,
}: JsonNodeProps) {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Children are always kept in the DOM inside this div. When collapsed we
  // set `hidden="until-found"` so the browser can find them with Ctrl+F even
  // while hidden. The `beforematch` event fires when the browser navigates to
  // a match inside a hidden section; we expand the node in response.
  //
  // We use useLayoutEffect to toggle the attribute synchronously before paint
  // so the visible tree and the DOM attribute are always in sync.
  const childrenDivRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = childrenDivRef.current;
    if (!el) return;
    if (expanded) {
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', 'until-found');
    }
  }, [expanded]);

  // flushSync forces all React commits caused by setExpanded to complete
  // before the event handler returns, so the DOM is fully stable when the
  // browser tries to paint the find-in-page highlight.
  useEffect(() => {
    const el = childrenDivRef.current;
    if (!el) return;
    const handler = () => {
      flushSync(() => setExpanded(true));
    };
    el.addEventListener('beforematch', handler);
    return () => el.removeEventListener('beforematch', handler);
  }, []);

  const comma = isLast ? '' : ',';

  const keyEl =
    keyName != null ? (
      <span className={styles.keyName}>{`"${keyName}"`}</span>
    ) : null;
  const colon = keyName != null ? <span>: </span> : null;

  if (keyName === 'ReferencedDocuments' && typeof value === 'string') {
    const parsedValue = parseReferencedDocuments(value);
    if (parsedValue !== value) {
      return (
        <JsonNode
          keyName={keyName}
          value={parsedValue}
          depth={depth}
          defaultExpanded={defaultExpanded}
          isLast={isLast}
        />
      );
    }
  }

  // null
  if (value === null) {
    return (
      <RowLine depth={depth} copyValue="null">
        {keyEl}
        {colon}
        <span className={styles.nullVal}>null</span>
        {comma}
      </RowLine>
    );
  }

  // string
  if (typeof value === 'string') {
    return (
      <RowLine depth={depth} copyValue={value}>
        {keyEl}
        {colon}
        <span className={styles.stringVal}>{JSON.stringify(value)}</span>
        {comma}
      </RowLine>
    );
  }

  // number
  if (typeof value === 'number') {
    return (
      <RowLine depth={depth} copyValue={String(value)}>
        {keyEl}
        {colon}
        <span className={styles.numberVal}>{value.toString()}</span>
        {comma}
      </RowLine>
    );
  }

  // boolean
  if (typeof value === 'boolean') {
    return (
      <RowLine depth={depth} copyValue={String(value)}>
        {keyEl}
        {colon}
        <span className={styles.boolVal}>{value.toString()}</span>
        {comma}
      </RowLine>
    );
  }

  // array
  if (Array.isArray(value)) {
    const copyValue = JSON.stringify(value, null, 2);

    if (value.length === 0) {
      return (
        <RowLine depth={depth} copyValue={copyValue}>
          {keyEl}
          {colon}
          <span>{'[]'}</span>
          {comma}
        </RowLine>
      );
    }

    return (
      <>
        {expanded ? (
          <RowLine
            depth={depth}
            copyValue={copyValue}
            collapsible
            collapsed={false}
            onToggle={() => setExpanded(false)}
          >
            {keyEl}
            {colon}
            <span>{'['}</span>
          </RowLine>
        ) : (
          <RowLine
            depth={depth}
            copyValue={copyValue}
            collapsible
            collapsed
            onToggle={() => setExpanded(true)}
          >
            {keyEl}
            {colon}
            <span>{'[ '}</span>
            <span className={styles.collapsedSummary}>
              {value.length.toString()} items
            </span>
            <span>{' ]'}</span>
            {comma}
          </RowLine>
        )}
        <div ref={childrenDivRef}>
          {value.map((item: unknown, i) => (
            <JsonNode
              key={i.toString()}
              value={item}
              depth={depth + 1}
              defaultExpanded={depth + 1 < 2}
              isLast={i === value.length - 1}
            />
          ))}
          {expanded && (
            <RowLine depth={depth} copyValue={copyValue}>
              <span>{']'}</span>
              {comma}
            </RowLine>
          )}
        </div>
      </>
    );
  }

  // object
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    const copyValue = JSON.stringify(value, null, 2);

    if (entries.length === 0) {
      return (
        <RowLine depth={depth} copyValue={copyValue}>
          {keyEl}
          {colon}
          <span>{'{}'}</span>
          {comma}
        </RowLine>
      );
    }

    return (
      <>
        {expanded ? (
          <RowLine
            depth={depth}
            copyValue={copyValue}
            collapsible
            collapsed={false}
            onToggle={() => setExpanded(false)}
          >
            {keyEl}
            {colon}
            <span>{'{'}</span>
          </RowLine>
        ) : (
          <RowLine
            depth={depth}
            copyValue={copyValue}
            collapsible
            collapsed
            onToggle={() => setExpanded(true)}
          >
            {keyEl}
            {colon}
            <span>{'{ '}</span>
            <span className={styles.collapsedSummary}>
              {entries.length.toString()} keys
            </span>
            <span>{' }'}</span>
            {comma}
          </RowLine>
        )}
        <div ref={childrenDivRef}>
          {entries.map(([k, v], i) => (
            <JsonNode
              key={k}
              keyName={k}
              value={v}
              depth={depth + 1}
              defaultExpanded={depth + 1 < 2}
              isLast={i === entries.length - 1}
            />
          ))}
          {expanded && (
            <RowLine depth={depth} copyValue={copyValue}>
              <span>{'}'}</span>
              {comma}
            </RowLine>
          )}
        </div>
      </>
    );
  }

  // fallback
  return (
    <RowLine depth={depth} copyValue={JSON.stringify(value)}>
      {keyEl}
      {colon}
      {JSON.stringify(value)}
      {comma}
    </RowLine>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function RawJsonViewer({
  data,
  filename = 'debug.json',
}: RawJsonViewerProps) {
  const styles = useStyles();
  const { copied, copy } = useCopyToClipboard();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const jsonStr = JSON.stringify(data, null, 2);

  const handleDownload = useCallback(() => {
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [jsonStr, filename]);

  const toolbarButtons = (
    <>
      <Button
        appearance="subtle"
        icon={<DownloadIcon />}
        onClick={handleDownload}
        aria-label="Download raw JSON"
      >
        Download JSON
      </Button>
      <Button
        appearance="subtle"
        icon={<CopyIcon />}
        onClick={() => copy(jsonStr)}
        aria-label="Copy raw JSON to clipboard"
      >
        {copied ? 'Copied!' : 'Copy JSON'}
      </Button>
    </>
  );

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <span className={styles.label}>Raw Signal</span>
        <div className={styles.toolbarActions}>
          {toolbarButtons}
          <Button
            appearance="subtle"
            icon={<MaximizeIcon />}
            onClick={() => setIsModalOpen(true)}
            aria-label="Maximize JSON viewer"
          />
        </div>
      </div>
      <div className={styles.treeContainer}>
        <JsonNode value={data} depth={0} defaultExpanded={true} isLast={true} />
      </div>

      <MaximizeDialog
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        title={filename}
        actions={<div className={styles.toolbarActions}>{toolbarButtons}</div>}
      >
        <div className={styles.treeContainer}>
          <JsonNode
            value={data}
            depth={0}
            defaultExpanded={true}
            isLast={true}
          />
        </div>
      </MaximizeDialog>
    </div>
  );
}
